import React, { useState, useEffect, useRef, useCallback } from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  Fade,
  Backdrop,
} from "@mui/material";
import {
  Send,
  Search,
  MoreVert,
  AttachFile,
  InsertEmoticon,
  Phone,
  VideoCall,
  Mic,
  MicOff,
  VolumeUp,
  VolumeOff,
  Videocam,
  VideocamOff,
  ScreenShare,
  Close,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import Avatar from "../../components/shared/Avatar";
import {
  setActiveContact,
  addLocalMessage,
  receiveMessage,
  setTyping,
  AUTO_REPLIES,
  fetchChatContacts,
  fetchMessages,
  sendMessageAsync,
  markMessagesReadAsync,
  ChatContact,
} from "../../store/slices/chatSlice";

// ─── Emoji Picker Data ─────────────────────────────────────────────────────────
const EMOJIS = [
  "😀","😂","🥰","😍","🤩","😎","🥳","😅","😊","😇",
  "👍","🙌","🔥","❤️","✨","🎉","💯","👏","🚀","💪",
  "😢","😮","😴","🤔","😬","🙄","😤","😡","🥺","😱",
  "🌟","💡","📌","✅","❌","⚡","🎯","💬","📞","📹",
];

const COLOR_MAP: Record<string, string> = {
  blue:   "#3b82f6",
  teal:   "#14b8a6",
  purple: "#8b5cf6",
  amber:  "#f59e0b",
  rose:   "#f43f5e",
  indigo: "#6366f1",
  green:  "#22c55e",
};

function fmtTimer(s: number) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

// ─── Typing Dots ───────────────────────────────────────────────────────────────
const TypingDots: React.FC = () => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: 0.4, p: "12px 16px",
    background: "#fff", borderRadius: "18px 18px 18px 4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)", width: "fit-content",
  }}>
    {[0, 1, 2].map(i => (
      <Box key={i} sx={{
        width: 7, height: 7, borderRadius: "50%", background: "#94a3b8",
        animation: "bounce 1.2s infinite",
        animationDelay: `${i * 0.2}s`,
        "@keyframes bounce": {
          "0%, 60%, 100%": { transform: "translateY(0)" },
          "30%": { transform: "translateY(-5px)" },
        },
      }} />
    ))}
  </Box>
);

import { useSocket } from "../../context/SocketContext";

// ─── Voice Call Modal ──────────────────────────────────────────────────────────
interface VoiceCallModalProps {
  contact: ChatContact;
  onClose: () => void;
}
const VoiceCallModal: React.FC<VoiceCallModalProps> = ({ contact, onClose }) => {
  const [phase, setPhase] = useState<"calling" | "connected" | "rejected">("calling");
  const [muted, setMuted] = useState(false);
  const [speakerOff, setSpeakerOff] = useState(false);
  const [timer, setTimer] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  const { socket } = useSocket();
  const user = useAppSelector(s => s.auth.user);
  
  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => { streamRef.current = stream; })
      .catch(() => {});

    if (socket && user) {
      socket.emit("call-user", {
        receiverId: contact._id,
        type: "voice",
        caller: { _id: user._id, name: user.name || user.username, initials: user.initials || "U", color: user.avColor || "blue" }
      });

      socket.on("call-accepted", () => {
        setPhase("connected");
        timerRef.current = setInterval(() => setTimer(s => s + 1), 1000);
      });

      socket.on("call-rejected", () => {
        setPhase("rejected");
        setTimeout(onClose, 2000);
      });
    }

    const t = setTimeout(() => {
      if (phase === "calling" && socket && user) {
        socket.emit("missed-call", {
          receiverId: contact._id,
          callerId: user._id,
          callerName: user.name || user.username,
          type: "voice"
        });
        setPhase("rejected"); // Or missed
        setTimeout(onClose, 2000);
      }
    }, 15000); // 15 seconds ring time

    return () => {
      clearTimeout(t);
      if (timerRef.current) clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      if (socket) {
        socket.emit("end-call", { to: contact._id });
        socket.off("call-accepted");
        socket.off("call-rejected");
      }
    };
  }, []);

  const handleMute = () => {
    setMuted(m => {
      const next = !m;
      streamRef.current?.getAudioTracks().forEach(t => { t.enabled = !next; });
      return next;
    });
  };

  const avatarColor = COLOR_MAP[contact.color] || "#3b82f6";

  return (
    <Backdrop open sx={{ zIndex: 2000, backdropFilter: "blur(12px)", background: "rgba(10,15,30,0.75)" }}>
      <Fade in>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, userSelect: "none" }}>
          <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {phase === "calling" && [1, 2, 3].map(i => (
              <Box key={i} sx={{
                position: "absolute", borderRadius: "50%",
                border: `2px solid ${avatarColor}`,
                animation: `pulseRing 2s ${i * 0.5}s infinite`,
                "@keyframes pulseRing": {
                  "0%": { width: 88, height: 88, opacity: 0.8 },
                  "100%": { width: 88 + i * 40, height: 88 + i * 40, opacity: 0 },
                },
              }} />
            ))}
            <Box sx={{
              width: 88, height: 88, borderRadius: "50%",
              background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}aa)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 30, fontWeight: 700, color: "#fff",
              boxShadow: `0 0 40px ${avatarColor}66`,
            }}>
              {contact.avatar}
            </Box>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#fff", mb: 0.5 }}>
              {contact.name}
            </Typography>
            <Typography sx={{ fontSize: 14, color: phase === "calling" ? "#f59e0b" : phase === "rejected" ? "#ef4444" : "#22c55e", fontWeight: 500 }}>
              {phase === "calling" ? "Ringing…" : phase === "rejected" ? "No answer" : fmtTimer(timer)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2.5, mt: 2 }}>
            <Tooltip title={speakerOff ? "Speaker On" : "Speaker Off"}>
              <IconButton onClick={() => setSpeakerOff(v => !v)} sx={{
                width: 56, height: 56,
                background: speakerOff ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.08)",
                color: speakerOff ? "#f59e0b" : "#fff",
                "&:hover": { background: "rgba(255,255,255,0.2)" },
              }}>
                {speakerOff ? <VolumeOff /> : <VolumeUp />}
              </IconButton>
            </Tooltip>
            <Tooltip title={muted ? "Unmute" : "Mute"}>
              <IconButton onClick={handleMute} sx={{
                width: 56, height: 56,
                background: muted ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.08)",
                color: muted ? "#ef4444" : "#fff",
                "&:hover": { background: muted ? "rgba(239,68,68,0.3)" : "rgba(255,255,255,0.2)" },
              }}>
                {muted ? <MicOff /> : <Mic />}
              </IconButton>
            </Tooltip>
            <Tooltip title="End Call">
              <IconButton onClick={onClose} sx={{
                width: 56, height: 56, background: "#ef4444", color: "#fff",
                "&:hover": { background: "#dc2626" },
                transform: "rotate(135deg)",
              }}>
                <Phone />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>
    </Backdrop>
  );
};

// ─── Video Call Modal ──────────────────────────────────────────────────────────
interface VideoCallModalProps {
  contact: ChatContact;
  onClose: () => void;
}
const VideoCallModal: React.FC<VideoCallModalProps> = ({ contact, onClose }) => {
  const [phase, setPhase] = useState<"connecting" | "connected" | "rejected">("connecting");
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [timer, setTimer] = useState(0);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { socket } = useSocket();
  const user = useAppSelector(s => s.auth.user);

  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then(stream => {
        streamRef.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      })
      .catch(() => {});

    if (socket && user) {
      socket.emit("call-user", {
        receiverId: contact._id,
        type: "video",
        caller: { _id: user._id, name: user.name || user.username, initials: user.initials || "U", color: user.avColor || "blue" }
      });

      socket.on("call-accepted", () => {
        setPhase("connected");
        timerRef.current = setInterval(() => setTimer(s => s + 1), 1000);
      });

      socket.on("call-rejected", () => {
        setPhase("rejected");
        setTimeout(onClose, 2000);
      });
    }

    const t = setTimeout(() => {
      if (phase === "connecting" && socket && user) {
        socket.emit("missed-call", {
          receiverId: contact._id,
          callerId: user._id,
          callerName: user.name || user.username,
          type: "video"
        });
        setPhase("rejected");
        setTimeout(onClose, 2000);
      }
    }, 15000);

    return () => {
      clearTimeout(t);
      if (timerRef.current) clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      if (socket) {
        socket.emit("end-call", { to: contact._id });
        socket.off("call-accepted");
        socket.off("call-rejected");
      }
    };
  }, []);

  const toggleMute = () => {
    setMuted(m => {
      const next = !m;
      streamRef.current?.getAudioTracks().forEach(t => { t.enabled = !next; });
      return next;
    });
  };

  const toggleCamera = () => {
    setCameraOff(c => {
      const next = !c;
      streamRef.current?.getVideoTracks().forEach(t => { t.enabled = !next; });
      return next;
    });
  };

  const avatarColor = COLOR_MAP[contact.color] || "#3b82f6";

  return (
    <Backdrop open sx={{ zIndex: 2000, background: "rgba(5,8,20,0.92)" }}>
      <Fade in>
        <Box sx={{
          position: "relative", width: "min(900px, 95vw)", height: "min(580px, 90vh)",
          borderRadius: 4, overflow: "hidden",
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
        }}>
          {/* Remote video area */}
          <Box sx={{
            width: "100%", height: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Box sx={{ textAlign: "center", opacity: phase === "connecting" ? 1 : 0.4 }}>
              <Box sx={{
                width: 96, height: 96, borderRadius: "50%", mx: "auto", mb: 2,
                background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}88)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 32, fontWeight: 700, color: "#fff",
                animation: phase === "connecting" ? "vcPulse 1.5s infinite" : "none",
                "@keyframes vcPulse": {
                  "0%, 100%": { boxShadow: `0 0 0 0 ${avatarColor}55` },
                  "50%": { boxShadow: `0 0 0 24px transparent` },
                },
              }}>
                {contact.avatar}
              </Box>
              <Typography sx={{ color: "#fff", fontSize: phase === "connecting" ? 20 : 16, fontWeight: 600 }}>
                {contact.name}
              </Typography>
              {phase === "connecting" && (
                <Typography sx={{ color: "#f59e0b", fontSize: 13, mt: 0.5 }}>Ringing…</Typography>
              )}
              {phase === "rejected" && (
                <Typography sx={{ color: "#ef4444", fontSize: 13, mt: 0.5 }}>No answer</Typography>
              )}
            </Box>
          </Box>

          {/* Header */}
          <Box sx={{
            position: "absolute", top: 0, left: 0, right: 0, p: "12px 20px",
            background: "linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{contact.name}</Typography>
              <Typography sx={{ color: phase === "connecting" ? "#f59e0b" : phase === "rejected" ? "#ef4444" : "#22c55e", fontSize: 12 }}>
                {phase === "connecting" ? "Ringing…" : phase === "rejected" ? "No answer" : fmtTimer(timer)}
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: "#fff", background: "rgba(255,255,255,0.1)" }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Local PiP */}
          <Box sx={{
            position: "absolute", bottom: 90, right: 20,
            width: 160, height: 110, borderRadius: 2,
            overflow: "hidden", border: "2px solid rgba(255,255,255,0.2)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            background: cameraOff ? "#1e293b" : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {cameraOff
              ? <VideocamOff sx={{ color: "#64748b", fontSize: 32 }} />
              : <video ref={localVideoRef} autoPlay muted playsInline
                  style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
            }
            <Box sx={{ position: "absolute", bottom: 4, left: 6, fontSize: 10, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
              You
            </Box>
          </Box>

          {/* Controls */}
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            p: "16px 24px",
            background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 2,
          }}>
            <Tooltip title={muted ? "Unmute" : "Mute"}>
              <IconButton onClick={toggleMute} sx={{
                width: 52, height: 52,
                background: muted ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.12)",
                color: muted ? "#ef4444" : "#fff",
              }}>
                {muted ? <MicOff /> : <Mic />}
              </IconButton>
            </Tooltip>
            <Tooltip title="End Call">
              <IconButton onClick={onClose} sx={{
                width: 60, height: 60, background: "#ef4444", color: "#fff",
                "&:hover": { background: "#dc2626" }, transform: "rotate(135deg)",
              }}>
                <Phone />
              </IconButton>
            </Tooltip>
            <Tooltip title={cameraOff ? "Camera On" : "Camera Off"}>
              <IconButton onClick={toggleCamera} sx={{
                width: 52, height: 52,
                background: cameraOff ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.12)",
                color: cameraOff ? "#ef4444" : "#fff",
              }}>
                {cameraOff ? <VideocamOff /> : <Videocam />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Share Screen">
              <IconButton sx={{ width: 52, height: 52, background: "rgba(255,255,255,0.12)", color: "#fff" }}>
                <ScreenShare />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>
    </Backdrop>
  );
};

// ─── Main Chat Component ───────────────────────────────────────────────────────
const Chat: React.FC = () => {
  const dispatch = useAppDispatch();
  const { contacts, messages, activeContactId, typing, loading, loadingMessages } =
    useAppSelector(s => s.chat);
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [voiceCallOpen, setVoiceCallOpen] = useState(false);
  const [videoCallOpen, setVideoCallOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const activeContact = contacts.find(c => c.id === activeContactId) ?? contacts[0];
  const activeMessages = messages[activeContactId] ?? [];
  const isTyping = typing[activeContactId] ?? false;
  const filteredContacts = contacts.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  // Fetch contacts on mount
  useEffect(() => {
    dispatch(fetchChatContacts());
  }, [dispatch]);

  // Fetch messages when active contact changes
  useEffect(() => {
    if (activeContactId) {
      dispatch(fetchMessages(activeContactId));
      dispatch(markMessagesReadAsync(activeContactId));
    }
  }, [activeContactId, dispatch]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeMessages, isTyping]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || !activeContact) return;

    // Optimistic update — show instantly
    const optimisticId = `local_${Date.now()}`;
    const nowTime = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    dispatch(addLocalMessage({
      contactId: activeContactId,
      message: { id: optimisticId, senderId: "me", text, time: nowTime },
    }));
    setInput("");
    setShowEmoji(false);
    inputRef.current?.focus();

    // Send to backend; fallback to simulated reply if API down
    dispatch(sendMessageAsync({ receiverId: activeContact._id, text }))
      .unwrap()
      .catch(() => {
        const replies = AUTO_REPLIES.default;
        const reply = replies[Math.floor(Math.random() * replies.length)];
        dispatch(setTyping({ contactId: activeContactId, isTyping: true }));
        setTimeout(() => {
          dispatch(setTyping({ contactId: activeContactId, isTyping: false }));
          dispatch(receiveMessage({
            contactId: activeContactId,
            text: reply,
            time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
          }));
        }, 1500);
      });
  }, [input, activeContactId, activeContact, dispatch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleContactClick = (id: string) => {
    dispatch(setActiveContact(id));
    dispatch(fetchMessages(id));
    dispatch(markMessagesReadAsync(id));
    setShowEmoji(false);
  };

  const getPreview = (contactId: string) => {
    const msgs = messages[contactId] ?? [];
    if (msgs.length === 0) return "";
    const last = msgs[msgs.length - 1];
    return last.senderId === "me" ? `You: ${last.text}` : last.text;
  };

  if (!activeContact && !loading) return null;

  return (
    <>
      <SEO title="Chat" noindex />

      {voiceCallOpen && activeContact && (
        <VoiceCallModal contact={activeContact} onClose={() => setVoiceCallOpen(false)} />
      )}
      {videoCallOpen && activeContact && (
        <VideoCallModal contact={activeContact} onClose={() => setVideoCallOpen(false)} />
      )}

      <Card sx={{
        display: "flex",
        height: "calc(100vh - 120px)",
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        position: "relative",
      }}>

        {/* ── Sidebar ──────────────────────────────────────────────────── */}
        <Box sx={{
          width: 300, flexShrink: 0,
          borderRight: "1px solid #e5e7eb",
          display: "flex", flexDirection: "column",
          background: "#f8fafc",
        }}>
          <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb", background: "#fff" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 2 }}>Messages</Typography>
            <TextField
              fullWidth size="small"
              placeholder="Search chats…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 99, background: "#f1f5f9" } }}
            />
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>

            {/* Skeleton while loading */}
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, mb: 0.5 }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: "50%", background: "#e2e8f0",
                  animation: "shimmer 1.5s infinite",
                  "@keyframes shimmer": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.4 } },
                }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ height: 12, borderRadius: 1, background: "#e2e8f0", mb: 1, width: "60%", animation: "shimmer 1.5s infinite" }} />
                  <Box sx={{ height: 10, borderRadius: 1, background: "#e2e8f0", width: "80%", animation: "shimmer 1.5s infinite" }} />
                </Box>
              </Box>
            ))}

            {/* Empty state */}
            {!loading && filteredContacts.length === 0 && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>
                  {contacts.length === 0 ? "No contacts found" : "No results for search"}
                </Typography>
              </Box>
            )}

            {/* Contact rows */}
            {!loading && filteredContacts.map(c => {
              const isActive = activeContactId === c.id;
              const preview = getPreview(c.id);
              return (
                <Box
                  key={c.id}
                  onClick={() => handleContactClick(c.id)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: 1.5, borderRadius: 2, cursor: "pointer", mb: 0.5,
                    background: isActive ? "rgba(133,209,219,0.1)" : "transparent",
                    transition: "background 0.2s",
                    "&:hover": { background: isActive ? "rgba(133,209,219,0.12)" : "#f1f5f9" },
                  }}
                >
                  <Box sx={{ position: "relative", flexShrink: 0 }}>
                    <Avatar initials={c.avatar} color={c.color} size={44} />
                    {c.online && (
                      <Box sx={{
                        position: "absolute", bottom: 0, right: 0,
                        width: 12, height: 12, borderRadius: "50%",
                        background: "#22c55e", border: "2px solid #fff",
                      }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.3 }}>
                      <Typography sx={{
                        fontSize: 14, fontWeight: isActive ? 700 : 500, color: "#0f172a",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120,
                      }}>
                        {c.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: isActive ? "#85D1DB" : "#94a3b8", fontWeight: isActive ? 600 : 400, flexShrink: 0 }}>
                        {messages[c.id]?.length ? messages[c.id][messages[c.id].length - 1].time : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography sx={{
                        fontSize: 12, color: isActive ? "#85D1DB" : "#64748b",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        fontWeight: c.unread > 0 ? 600 : 400, maxWidth: 145,
                      }}>
                        {typing[c.id] ? "typing…" : preview}
                      </Typography>
                      {c.unread > 0 && (
                        <Box sx={{
                          background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 700,
                          px: 0.8, borderRadius: 99, lineHeight: 1.6, ml: 1, flexShrink: 0,
                        }}>
                          {c.unread}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── Main Chat Area ────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", background: "#fff", minWidth: 0 }}>

          {/* Header */}
          {activeContact && (
            <Box sx={{
              p: "12px 20px", borderBottom: "1px solid #e5e7eb",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "rgba(255,255,255,0.95)", backdropFilter: "blur(8px)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ position: "relative" }}>
                  <Avatar initials={activeContact.avatar} color={activeContact.color} size={40} />
                  {activeContact.online && (
                    <Box sx={{
                      position: "absolute", bottom: 0, right: 0,
                      width: 10, height: 10, borderRadius: "50%",
                      background: "#22c55e", border: "2px solid #fff",
                    }} />
                  )}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{activeContact.name}</Typography>
                  <Typography sx={{
                    fontSize: 11, fontWeight: 500,
                    color: isTyping ? "#3b82f6" : (activeContact.online ? "#16a34a" : "#94a3b8"),
                  }}>
                    {isTyping ? "typing…" : (activeContact.online ? "Online" : "Offline")}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Voice Call">
                  <IconButton
                    onClick={() => setVoiceCallOpen(true)}
                    sx={{ color: "#64748b", "&:hover": { color: "#22c55e", background: "rgba(34,197,94,0.08)" }, transition: "all 0.2s" }}
                  >
                    <Phone />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Video Call">
                  <IconButton
                    onClick={() => setVideoCallOpen(true)}
                    sx={{ color: "#64748b", "&:hover": { color: "#3b82f6", background: "rgba(59,130,246,0.08)" }, transition: "all 0.2s" }}
                  >
                    <VideoCall />
                  </IconButton>
                </Tooltip>
                <Tooltip title="More Info">
                  <IconButton sx={{ color: "#64748b" }}>
                    <MoreVert />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Messages */}
          <Box sx={{
            flex: 1, overflowY: "auto", p: 3,
            display: "flex", flexDirection: "column", gap: 1.5,
            background: "linear-gradient(to bottom right, #f8fafc, #f1f5f9)",
          }}>
            {/* Messages loading spinner */}
            {loadingMessages && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "50%",
                  border: "3px solid #e2e8f0", borderTopColor: "#85D1DB",
                  animation: "spin 0.8s linear infinite",
                  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                }} />
              </Box>
            )}

            {/* Message bubbles */}
            {!loadingMessages && activeMessages.map(msg => {
              const isMe = msg.senderId === "me";
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    justifyContent: isMe ? "flex-end" : "flex-start",
                    alignItems: "flex-end",
                    gap: 1,
                  }}
                >
                  {!isMe && activeContact && (
                    <Avatar initials={activeContact.avatar} color={activeContact.color} size={28} />
                  )}
                  <Box sx={{ maxWidth: "65%" }}>
                    <Box sx={{
                      p: "10px 15px",
                      background: isMe ? "linear-gradient(135deg, #85D1DB, #B6F2D1)" : "#fff",
                      color: isMe ? "#fff" : "#0f172a",
                      borderRadius: isMe ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      fontSize: 14, lineHeight: 1.45,
                    }}>
                      {msg.text}
                    </Box>
                    <Typography sx={{
                      fontSize: 10, color: "#94a3b8", mt: 0.4,
                      textAlign: isMe ? "right" : "left", mx: 1,
                    }}>
                      {msg.time}
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            {/* Typing indicator */}
            {isTyping && activeContact && (
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                <Avatar initials={activeContact.avatar} color={activeContact.color} size={28} />
                <TypingDots />
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Emoji Picker */}
          {showEmoji && (
            <Box sx={{
              position: "absolute", bottom: 80, right: 16,
              background: "#fff", borderRadius: 3, p: 1.5,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              display: "flex", flexWrap: "wrap", width: 260, gap: 0.5,
              zIndex: 100, border: "1px solid #e5e7eb",
            }}>
              {EMOJIS.map(e => (
                <Box
                  key={e}
                  onClick={() => { setInput(v => v + e); inputRef.current?.focus(); }}
                  sx={{
                    fontSize: 22, cursor: "pointer", borderRadius: 1, p: "4px",
                    transition: "transform 0.1s",
                    "&:hover": { transform: "scale(1.3)", background: "#f1f5f9" },
                  }}
                >
                  {e}
                </Box>
              ))}
            </Box>
          )}

          {/* Input Bar */}
          <Box sx={{
            p: "10px 16px", background: "#fff", borderTop: "1px solid #e5e7eb",
            display: "flex", gap: 1, alignItems: "center",
          }}>
            <Tooltip title="Attach File">
              <IconButton sx={{ color: "#94a3b8", "&:hover": { color: "#3b82f6" } }}>
                <AttachFile />
              </IconButton>
            </Tooltip>
            <TextField
              fullWidth
              placeholder="Type a message…"
              variant="outlined"
              size="small"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              inputRef={inputRef}
              multiline
              maxRows={3}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 99, background: "#f8fafc",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "#85D1DB" },
                  "&.Mui-focused fieldset": { borderColor: "#85D1DB" },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowEmoji(v => !v)}
                      sx={{ color: showEmoji ? "#85D1DB" : "#94a3b8", "&:hover": { color: "#85D1DB" } }}
                    >
                      <InsertEmoticon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {input.trim() ? (
              <IconButton
                onClick={handleSend}
                sx={{
                  background: "linear-gradient(135deg, #85D1DB, #B6F2D1)",
                  color: "#fff", width: 42, height: 42, flexShrink: 0,
                  "&:hover": { background: "linear-gradient(135deg, #6BB8C3, #95E5BB)", transform: "scale(1.05)" },
                  transition: "all 0.2s",
                  boxShadow: "0 4px 12px rgba(133,209,219,0.4)",
                }}
              >
                <Send sx={{ fontSize: 18 }} />
              </IconButton>
            ) : (
              <Tooltip title="Voice Message">
                <IconButton sx={{ color: "#64748b", "&:hover": { color: "#ef4444" } }}>
                  <Mic />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default Chat;
